import { useRef, useState } from "react";
import {
  AlignHorizontalDistributeCenter,
  AlignHorizontalJustifyCenter,
  AlignHorizontalJustifyEnd,
  AlignHorizontalJustifyStart,
  AlignVerticalDistributeCenter,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
  AlignVerticalJustifyStart,
  ArrowLeft,
  Eye,
  FolderOpen,
  Maximize2,
  Plus,
  RotateCcw,
  Save,
  StretchHorizontal,
  StretchVertical,
} from "lucide-react";
import { toast } from "sonner";
import { PD } from "@/constants/testIds";
import PageDesignerCanvas from "@/components/pageDesigner/PageDesignerCanvas";
import PageDesignerProperties from "@/components/pageDesigner/PageDesignerProperties";
import PageDesignerRtfToolbar from "@/components/pageDesigner/PageDesignerRtfToolbar";
import { useAuth } from "@/context/AuthContext";
import {
  CELL_SIZE,
  GRID_COLUMNS,
  GRID_ROWS,
  GRID_SIZE_LABEL,
  NO_GRID_SPACE_MESSAGE,
  addPlaceholderCard,
  alignCardsBottom,
  alignCardsCenterHorizontal,
  alignCardsCenterVertical,
  alignCardsLeft,
  alignCardsRight,
  alignCardsTop,
  defaultCards,
  distributeCardsHorizontally,
  distributeCardsVertically,
  duplicateCard,
  findCard,
  gridCssVars,
  gridSizeKey,
  layoutEquals,
  layoutHasOverlap,
  matchCardsHeight,
  matchCardsSize,
  matchCardsWidth,
  removeCard,
} from "@/lib/pageDesignerLayout";
import { isRtfCard } from "@/lib/pageDesignerRtf";
import { canWriteMainTab } from "@/lib/maintabAccess";
import {
  initialLayout,
  initialSavedSnapshot,
  loadSavedLayout,
  resetLayout,
  saveLayout,
} from "@/lib/pageDesignerStorage";
import "@/styles/emr-page-designer.css";

export default function PageDesignerPage() {
  const { user, isAdmin } = useAuth();
  const canWrite = canWriteMainTab(user?.maintabAccess, "page-designer", { isAdmin });
  const [cards, setCards] = useState(initialLayout);
  const [selectedIds, setSelectedIds] = useState([]);
  const [panel, setPanel] = useState("properties");
  const [saved, setSaved] = useState(initialSavedSnapshot);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const canvasRef = useRef(null);
  // Dirty state compares the one canonical geometry there is: the cards'
  // own grid cells. Save adopts exactly what it persisted, so a successful
  // Save always lands back on "saved".
  const dirty = saved ? !layoutEquals(cards, saved) : !layoutEquals(cards, defaultCards());
  // The Properties panel and RTF toolbar only ever show one card: the most
  // recently selected one, whether the selection has one card or several.
  const selectedId = selectedIds.length ? selectedIds[selectedIds.length - 1] : null;
  const selectedCard = findCard(cards, selectedId);
  const rtfToolbarEnabled = Boolean(canWrite && selectedCard && isRtfCard(selectedCard));

  /** Replaces the whole selection with a single card, or clears it. */
  const selectOnly = (id) => {
    setSelectedIds(id ? [id] : []);
  };

  /** Canvas click handling: plain click replaces, shift/ctrl/cmd-click toggles membership. */
  const onCanvasSelect = (id, { additive = false } = {}) => {
    if (!id) {
      setSelectedIds([]);
      return;
    }
    if (!additive) {
      setSelectedIds([id]);
      return;
    }
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((existing) => existing !== id) : [...current, id],
    );
  };

  const onSave = () => {
    if (!canWrite) return;
    if (layoutHasOverlap(cards)) {
      toast.error("Cards overlap. Adjust the layout before saving.");
      return;
    }
    const persisted = saveLayout(cards);
    if (!persisted) {
      toast.error("This device can't save the page layout.");
      return;
    }
    // Adopt what actually reached storage as both the live layout and the
    // saved baseline, so on-screen state, snapshot and storage are the
    // same geometry and the page returns to "saved".
    setCards(persisted);
    setSaved(persisted.map((card) => ({ ...card })));
    // Normalization repairs geometry, so a card can only ever be dropped
    // here if its identity was unusable. Never report that as a clean save.
    if (persisted.length < cards.length) {
      toast.error(
        `Saved, but ${cards.length - persisted.length} unreadable card(s) could not be stored.`,
      );
      return;
    }
    toast.success("Page layout saved on this device.");
  };

  const onLoad = () => {
    if (!canWrite) return;
    const loaded = loadSavedLayout();
    if (!loaded) {
      toast.info("No saved page layout on this device.");
      return;
    }
    setCards(loaded);
    setSaved(loaded.map((card) => ({ ...card })));
    setSelectedIds([]);
    toast.success("Saved page layout loaded.");
  };

  const onReset = () => {
    if (!canWrite) return;
    setCards(resetLayout());
    setSelectedIds([]);
    toast.success("Page layout reset to the blank grid.");
  };

  const onAddCard = () => {
    if (!canWrite) return;
    const next = addPlaceholderCard(cards);
    if (!next) {
      toast.error(NO_GRID_SPACE_MESSAGE);
      return;
    }
    const created = next[next.length - 1];
    setCards(next);
    selectOnly(created.id);
    setPanel("properties");
  };

  const onRemoveCard = (id) => {
    if (!canWrite) return;
    const target = findCard(cards, id);
    const next = removeCard(cards, id);
    if (!next || !target) return;
    setCards(next);
    setSelectedIds([]);
    toast.success(`${target.title} removed from the layout.`);
  };

  const onDuplicateCard = (id) => {
    if (!canWrite) return;
    const next = duplicateCard(cards, id);
    if (!next) {
      toast.error(NO_GRID_SPACE_MESSAGE);
      return;
    }
    const created = next[next.length - 1];
    setCards(next);
    selectOnly(created.id);
    setPanel("properties");
  };

  const onPreview = () => {
    setIsPreviewMode(true);
  };

  const onBackToDesigner = () => {
    setIsPreviewMode(false);
  };

  const ALIGN_ACTIONS = [
    { key: "left", testId: PD.alignLeftBtn, label: "Align Left", icon: AlignHorizontalJustifyStart, run: alignCardsLeft },
    { key: "right", testId: PD.alignRightBtn, label: "Align Right", icon: AlignHorizontalJustifyEnd, run: alignCardsRight },
    { key: "top", testId: PD.alignTopBtn, label: "Align Top", icon: AlignVerticalJustifyStart, run: alignCardsTop },
    { key: "bottom", testId: PD.alignBottomBtn, label: "Align Bottom", icon: AlignVerticalJustifyEnd, run: alignCardsBottom },
    {
      key: "center-horizontal",
      testId: PD.alignCenterHorizontalBtn,
      label: "Center Horizontally",
      icon: AlignHorizontalJustifyCenter,
      run: alignCardsCenterHorizontal,
    },
    {
      key: "center-vertical",
      testId: PD.alignCenterVerticalBtn,
      label: "Center Vertically",
      icon: AlignVerticalJustifyCenter,
      run: alignCardsCenterVertical,
    },
  ];
  const canAlign = canWrite && selectedIds.length >= 2;

  const onAlign = (align) => {
    if (!canAlign) return;
    const next = align(cards, selectedIds);
    if (!next) {
      toast.error("Can't align: the result would overlap another card or leave the grid.");
      return;
    }
    setCards(next);
  };

  const DISTRIBUTE_ACTIONS = [
    {
      key: "distribute-horizontal",
      testId: PD.distributeHorizontalBtn,
      label: "Distribute Horizontally",
      icon: AlignHorizontalDistributeCenter,
      run: distributeCardsHorizontally,
    },
    {
      key: "distribute-vertical",
      testId: PD.distributeVerticalBtn,
      label: "Distribute Vertically",
      icon: AlignVerticalDistributeCenter,
      run: distributeCardsVertically,
    },
  ];
  const canDistribute = canWrite && selectedIds.length >= 3;

  const onDistribute = (distribute) => {
    if (!canDistribute) return;
    const next = distribute(cards, selectedIds);
    if (!next) {
      toast.error("Can't distribute: the result would overlap another card or leave the grid.");
      return;
    }
    setCards(next);
  };

  const SAME_SIZE_ACTIONS = [
    { key: "same-width", testId: PD.sameWidthBtn, label: "Same Width", icon: StretchHorizontal, run: matchCardsWidth },
    { key: "same-height", testId: PD.sameHeightBtn, label: "Same Height", icon: StretchVertical, run: matchCardsHeight },
    { key: "same-size", testId: PD.sameSizeBtn, label: "Same Size", icon: Maximize2, run: matchCardsSize },
  ];

  const onMatchSize = (match) => {
    if (!canAlign || !selectedId) return;
    const next = match(cards, selectedIds, selectedId);
    if (!next) {
      toast.error("Can't match size: the result would overlap another card or leave the grid.");
      return;
    }
    setCards(next);
  };

  return (
    <div
      data-testid={PD.page}
      className={`epd${isPreviewMode ? " epd--preview" : ""}`}
      data-preview-mode={isPreviewMode ? "true" : "false"}
      data-grid-columns={GRID_COLUMNS}
      data-grid-rows={GRID_ROWS}
      data-cell-size={CELL_SIZE}
      style={gridCssVars()}
    >
      <div className="epd__pagehead">
        <div>
          <div className="epd__title-row">
            <h1 data-testid={PD.title} className="epd__title">
              Page Designer
            </h1>
            <span className="epd__badge">{isPreviewMode ? "Preview" : "V1"}</span>
          </div>
          <p className="epd__subtitle">
            {isPreviewMode
              ? "Previewing the current in-memory layout. Nothing is saved by entering or leaving Preview."
              : `Design and arrange page modules on a ${GRID_COLUMNS} × ${GRID_ROWS} square-cell grid.`}
          </p>
        </div>
        <div className="epd__actions">
          {isPreviewMode ? (
            <button
              type="button"
              data-testid={PD.previewExitBtn}
              className="epd__btn epd__btn--ghost"
              onClick={onBackToDesigner}
            >
              <ArrowLeft className="epd__btn-icon" aria-hidden="true" />
              Back to Designer
            </button>
          ) : (
            <>
              <select
                data-testid={PD.gridSize}
                className="epd__grid-size browser-default"
                value={gridSizeKey()}
                aria-label="Grid size"
                disabled
              >
                <option value={gridSizeKey()}>Grid: {GRID_SIZE_LABEL.replace(" Grid", "")}</option>
              </select>
              <button
                type="button"
                data-testid={PD.addCardBtn}
                className="epd__btn"
                disabled={!canWrite}
                onClick={onAddCard}
              >
                <Plus className="epd__btn-icon" aria-hidden="true" />
                Add New Card
              </button>
              <button
                type="button"
                data-testid={PD.loadBtn}
                className="epd__btn epd__btn--ghost"
                disabled={!canWrite}
                onClick={onLoad}
              >
                <FolderOpen className="epd__btn-icon" aria-hidden="true" />
                Load Layout
              </button>
              <button
                type="button"
                data-testid={PD.resetBtn}
                className="epd__btn epd__btn--ghost"
                disabled={!canWrite}
                onClick={onReset}
              >
                <RotateCcw className="epd__btn-icon" aria-hidden="true" />
                Reset
              </button>
              {ALIGN_ACTIONS.map(({ key, testId, label, icon: Icon, run }) => (
                <button
                  key={key}
                  type="button"
                  data-testid={testId}
                  className="epd__btn epd__btn--ghost"
                  aria-label={label}
                  title={label}
                  disabled={!canAlign}
                  onClick={() => onAlign(run)}
                >
                  <Icon className="epd__btn-icon" aria-hidden="true" />
                </button>
              ))}
              {DISTRIBUTE_ACTIONS.map(({ key, testId, label, icon: Icon, run }) => (
                <button
                  key={key}
                  type="button"
                  data-testid={testId}
                  className="epd__btn epd__btn--ghost"
                  aria-label={label}
                  title={label}
                  disabled={!canDistribute}
                  onClick={() => onDistribute(run)}
                >
                  <Icon className="epd__btn-icon" aria-hidden="true" />
                </button>
              ))}
              {SAME_SIZE_ACTIONS.map(({ key, testId, label, icon: Icon, run }) => (
                <button
                  key={key}
                  type="button"
                  data-testid={testId}
                  className="epd__btn epd__btn--ghost"
                  aria-label={label}
                  title={label}
                  disabled={!canAlign}
                  onClick={() => onMatchSize(run)}
                >
                  <Icon className="epd__btn-icon" aria-hidden="true" />
                </button>
              ))}
              <PageDesignerRtfToolbar
                cardId={selectedCard?.id || null}
                enabled={rtfToolbarEnabled}
              />
              <button
                type="button"
                data-testid={PD.previewBtn}
                className="epd__btn epd__btn--ghost"
                onClick={onPreview}
              >
                <Eye className="epd__btn-icon" aria-hidden="true" />
                Preview
              </button>
              <button
                type="button"
                data-testid={PD.saveBtn}
                className="epd__btn"
                disabled={!canWrite}
                onClick={onSave}
              >
                <Save className="epd__btn-icon" aria-hidden="true" />
                Save Layout
              </button>
            </>
          )}
        </div>
      </div>

      <div className="epd__workspace">
        <PageDesignerCanvas
          cards={cards}
          selectedIds={selectedIds}
          disabled={!canWrite || isPreviewMode}
          preview={isPreviewMode}
          canvasRef={canvasRef}
          onSelect={onCanvasSelect}
          onCardsChange={setCards}
        />
        {isPreviewMode ? null : (
          <PageDesignerProperties
            cards={cards}
            selectedId={selectedId}
            panel={panel}
            disabled={!canWrite}
            onPanelChange={setPanel}
            onSelect={selectOnly}
            onCardsChange={setCards}
            onAddCard={onAddCard}
            onRemoveCard={onRemoveCard}
            onDuplicateCard={onDuplicateCard}
          />
        )}
      </div>
      <p data-testid={PD.status} className="sr-only">
        {dirty ? "dirty" : "saved"}
      </p>
    </div>
  );
}
